import { createContext, useContext, useRef, useState } from "react";
import api, { API } from "@/lib/api";

const PlayerContext = createContext(null);

export const coverUrl = (b) => (b?.has_cover ? `${API}/beats/${b.beat_id}/cover?v=${b.cover_version || ""}` : null);

export const PlayerProvider = ({ children }) => {
  const audioRef = useRef();
  const [track, setTrack] = useState(null);
  const [queue, setQueue] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(0);
  const countedRef = useRef(false);
  const listenedRef = useRef(0);
  const lastTimeRef = useRef(0);

  const playBeat = (beat) => {
    const a = audioRef.current;
    countedRef.current = false;
    listenedRef.current = 0;
    lastTimeRef.current = 0;
    setTrack(beat);
    setTime(0);
    setDur(0);
    a.src = `${API}/beats/${beat.beat_id}/stream`;
    a.play().catch(() => {});
  };
  const toggle = (beat) => {
    if (beat && beat.beat_id !== track?.beat_id) return playBeat(beat);
    audioRef.current.paused ? audioRef.current.play().catch(() => {}) : audioRef.current.pause();
  };
  const step = (dir) => {
    if (!queue.length) return;
    const idx = queue.findIndex((b) => b.beat_id === track?.beat_id);
    const nextIdx = idx === -1 ? 0 : (idx + dir + queue.length) % queue.length;
    playBeat(queue[nextIdx]);
  };
  const next = () => step(1);
  const prev = () => {
    if (audioRef.current.currentTime > 3) { audioRef.current.currentTime = 0; return; }
    step(-1);
  };
  const onTime = (e) => {
    const t = e.target.currentTime;
    setTime(t);
    const delta = t - lastTimeRef.current;
    lastTimeRef.current = t;
    if (delta > 0 && delta < 1.5) listenedRef.current += delta;
    if (!countedRef.current && track && listenedRef.current >= 10) {
      countedRef.current = true;
      api.post(`/beats/${track.beat_id}/play`).catch(() => {});
    }
  };
  const seek = (ratio) => { if (dur) audioRef.current.currentTime = ratio * dur; };
  const close = () => { audioRef.current.pause(); audioRef.current.removeAttribute("src"); setTrack(null); setPlaying(false); };
  const syncTrack = (beat) => { if (beat.beat_id === track?.beat_id) setTrack(beat); };

  return (
    <PlayerContext.Provider value={{ track, playing, time, dur, toggle, seek, close, syncTrack, next, prev, setQueue, queueSize: queue.length }}>
      {children}
      <audio ref={audioRef} data-beat-audio preload="metadata" data-testid="global-beat-audio"
        onContextMenu={(e) => e.preventDefault()}
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
        onEnded={() => (queue.length ? next() : setPlaying(false))}
        onTimeUpdate={onTime} onLoadedMetadata={(e) => setDur(e.target.duration)} />
    </PlayerContext.Provider>
  );
};

export const usePlayer = () => useContext(PlayerContext);
